import { TablePagination, type PaginationState } from '@/components/shared/TablePagination';
import { Badge } from '@/components/ui-kit/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Log {
  id: string;
  templateSlug: string | null;
  endpoint: string;
  model: string;
  totalTokens: number;
  cost: number;
  responseTime: number;
  success: boolean;
  createdAt: string;
}

interface LogsTableProps {
  logs: Log[];
  pagination: PaginationState;
  onPageChange: (page: number) => void;
  loading?: boolean;
}

export function LogsTable({ logs, pagination, onPageChange, loading }: LogsTableProps) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-gray-400 animate-pulse overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_TIME')}</TableHead>
                <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_TEMPLATE')}</TableHead>
                <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_MODEL')}</TableHead>
                <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_TOKENS')}</TableHead>
                <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_COST')}</TableHead>
                <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_LATENCY')}</TableHead>
                <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_STATUS')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="h-4 w-24 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-32 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-16 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-16 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-16 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-16 bg-muted rounded" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-gray-300 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_TIME')}</TableHead>
            <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_TEMPLATE')}</TableHead>
            <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_MODEL')}</TableHead>
            <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_TOKENS')}</TableHead>
            <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_COST')}</TableHead>
            <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_LATENCY')}</TableHead>
            <TableHead>{t('ADMIN_ANALYTICS_LOGSTABLE_HEADERS_STATUS')}</TableHead>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  {t('ADMIN_ANALYTICS_LOGSTABLE_EMPTY')}
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="whitespace-nowrap">
                    {formatDistanceToNow(new Date(log.createdAt), {
                      addSuffix: true,
                    })}
                  </TableCell>
                  <TableCell>
                    {log.templateSlug ? (
                      <Badge variant="outline">{log.templateSlug}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{log.model}</TableCell>
                  <TableCell>{log.totalTokens.toLocaleString()}</TableCell>
                  <TableCell>${log.cost.toFixed(5)}</TableCell>
                  <TableCell>{log.responseTime}ms</TableCell>
                  <TableCell>
                    {log.success ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination pagination={pagination} onPageChange={onPageChange} />
    </div>
  );
}
